import {
  actionResponseSchema,
  cancellableSaleResponseSchema,
  cancellationDecisionResponseSchema,
  cancellationExecutionResponseSchema,
  cancellationRequestResponseSchema,
  cancellationRequestsResponseSchema,
  creditNoteSchema,
  getCancellationAccess,
  type CancellableSale,
  type CancellationRequest,
  type SessionIdentity,
} from "@cosmo/contracts";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { downloadPdf, printReceiptPdf } from "./local-print";
import { buildCancellationTicketPdf } from "./ticket-pdf";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" });
const eventDate = (value: string) => new Intl.DateTimeFormat("es-MX", {
  dateStyle: "short", timeStyle: "medium", timeZone: "America/Hermosillo",
}).format(new Date(value));

const statusLabels: Record<CancellationRequest["status"], string> = {
  PENDING: "Pendiente de revisión", APPROVED: "Autorizada", REJECTED: "Rechazada",
  EXPIRED: "Clave vencida", EXECUTED: "Cancelación aplicada",
};

async function responseMessage(response: Response): Promise<string> {
  try {
    const body = await response.json() as { error?: { message?: string } };
    return body.error?.message || "No fue posible completar la operación.";
  } catch {
    return "No fue posible completar la operación.";
  }
}

function cleanText(value: string): string {
  return value.replace(/·/g, "-").replace(/–/g, "-").replace(/—/g, "-");
}

async function cancellationPdf(request: CancellationRequest, documentType: "CANCELLATION_TICKET" | "CREDIT_NOTE", redemptionCode?: string): Promise<Uint8Array> {
  const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
  const document = await PDFDocument.create();
  const regular = await document.embedFont(StandardFonts.Helvetica);
  const bold = await document.embedFont(StandardFonts.HelveticaBold);
  const note = request.creditNote;
  const title = documentType === "CREDIT_NOTE" ? "NOTA DE CREDITO" : "COMPROBANTE DE CANCELACION";
  const lines = [
    { text: "COSMO OPTICAS", strong: true, center: true },
    { text: title, strong: true, center: true },
    { text: documentType === "CREDIT_NOTE" ? note?.folio || request.folio : request.folio, strong: true, center: true },
    { text: `${request.branchName} #${request.branchCode}`, center: true },
    { text: "--------------------------------" },
    { text: `Orden cancelada: ${request.saleFolio}`, strong: true },
    { text: `Cliente: ${request.customerName}` },
    { text: `Tipo: ${request.operationalType === "WARRANTY" ? "Garantia - con merma de lentes" : "Administrativa - sin merma de lentes"}` },
    { text: `Motivo: ${request.reason}` },
    { text: `Armazon reintegrado: ${request.frameReturnRequired ? "SI" : "NO APLICA"}` },
    { text: "Lentes reintegrados: NO", strong: true },
    { text: "--------------------------------" },
    { text: `Importe cancelado: ${money.format(request.authorizedAmount)}`, strong: true },
    ...(documentType === "CREDIT_NOTE" && note ? [
      { text: `Destino: ${note.destination === "REFUND" ? "Devolucion solicitada" : "Traspaso a nueva orden"}` },
      { text: `Saldo disponible: ${money.format(note.balance)}`, strong: true },
      { text: `Estado: ${note.status}` },
      ...(redemptionCode ? [
        { text: "CODIGO UNICO DE APLICACION", strong: true, center: true },
        { text: redemptionCode, strong: true, center: true },
        { text: "Conserve este codigo. Solo puede aplicarse en una orden nueva y se valida contra su saldo vigente.", strong: true },
      ] : []),
    ] : []),
    { text: "--------------------------------" },
    { text: `Solicito: ${request.requestedBy}` },
    { text: `Autorizo: ${request.decidedBy || "Pendiente"}` },
    { text: `Ejecuto: ${request.executedBy || "Pendiente"}` },
    { text: "Documento de control interno", center: true },
  ];
  const wrapped = lines.flatMap((line) => {
    const words = cleanText(line.text).split(/\s+/);
    const rows: string[] = [];
    let current = "";
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (candidate.length > 35 && current) { rows.push(current); current = word; }
      else current = candidate;
    }
    if (current) rows.push(current);
    return rows.map((text) => ({ ...line, text }));
  });
  const width = 226.77;
  const height = Math.max(360, 48 + wrapped.length * 13);
  const page = document.addPage([width, height]);
  let y = height - 25;
  for (const line of wrapped) {
    const font = line.strong ? bold : regular;
    const size = line.strong ? 9 : 8;
    const measured = font.widthOfTextAtSize(line.text, size);
    page.drawText(line.text, {
      x: line.center ? Math.max(10, (width - measured) / 2) : 12,
      y, size, font, color: rgb(0.03, 0.12, 0.2),
    });
    y -= 13;
  }
  return document.save();
}

export function CancellationOperations({ session, onClose, initialSaleFolio = "" }: {
  session: SessionIdentity; onClose: () => void; initialSaleFolio?: string;
}) {
  const [items, setItems] = useState<CancellationRequest[]>([]);
  const [filter, setFilter] = useState("ALL");
  const [message, setMessage] = useState("");
  const [busy, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const loading = busy || listLoading;
  const [saleFolio, setSaleFolio] = useState(initialSaleFolio);
  const [scopeFolio, setScopeFolio] = useState(initialSaleFolio);
  const [sale, setSale] = useState<CancellableSale | null>(null);
  const [operationalType, setOperationalType] = useState<"ADMINISTRATIVE" | "WARRANTY">("ADMINISTRATIVE");
  const [financialDestination, setFinancialDestination] = useState<"REFUND" | "TRANSFER_NEW_ORDER">("REFUND");
  const [reason, setReason] = useState("");
  const [decisionComments, setDecisionComments] = useState<Record<string, string>>({});
  const [codes, setCodes] = useState<Record<string, string>>({});
  const [executeCodes, setExecuteCodes] = useState<Record<string, string>>({});
  const [frameReceived, setFrameReceived] = useState<Record<string, boolean>>({});
  const [redemptionCodes, setRedemptionCodes] = useState<Record<string, string>>({});
  const [creditCode, setCreditCode] = useState("");
  const [destinationSale, setDestinationSale] = useState("");
  const [creditAmount, setCreditAmount] = useState("");
  const [refundReferences, setRefundReferences] = useState<Record<string, string>>({});
  const [executionConfirmed, setExecutionConfirmed] = useState<Record<string, boolean>>({});

  const { canRequest, canApprove, canExecute } = getCancellationAccess(session);
  const canConfirmRefund = canApprove && session.roles.includes("ADMINISTRADOR");

  const load = useCallback(async (signal?: AbortSignal) => {
    setListLoading(true);
    try {
      const query = new URLSearchParams({ status: filter });
      if (scopeFolio) query.set("saleFolio", scopeFolio);
      const response = await fetch(`${apiBaseUrl}/api/v1/cancellations?${query}`, {
        credentials: "include", headers: { Accept: "application/json" }, cache: "no-store", signal: signal ?? null,
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const result = cancellationRequestsResponseSchema.parse(await response.json()).data;
      if (!signal?.aborted) setItems(result);
    } catch (cause) {
      if (!signal?.aborted) setMessage(cause instanceof Error ? cause.message : "No fue posible consultar las cancelaciones.");
    } finally {
      if (!signal?.aborted) setListLoading(false);
    }
  }, [filter, scopeFolio]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const lookupSale = useCallback(async (folio: string) => {
    setLoading(true); setSale(null); setMessage(""); setReason("");
    try {
      const related = await fetch(`${apiBaseUrl}/api/v1/cancellations?status=ALL&saleFolio=${encodeURIComponent(folio.trim())}`, {
        credentials: "include", headers: { Accept: "application/json" }, cache: "no-store",
      });
      if (!related.ok) throw new Error(await responseMessage(related));
      const existing = cancellationRequestsResponseSchema.parse(await related.json()).data
        .find((item) => ["PENDING", "APPROVED", "EXECUTED"].includes(item.status));
      if (existing) {
        setScopeFolio(existing.saleFolio); setSaleFolio(existing.saleFolio); setFilter("ALL");
        setItems([existing]);
        setMessage(`${existing.folio}: ${statusLabels[existing.status]}. Consulta la solicitud existente en la bandeja; no se creó otra.`);
        return;
      }
      const response = await fetch(`${apiBaseUrl}/api/v1/cancellations/sales/${encodeURIComponent(folio.trim())}`, {
        credentials: "include", headers: { Accept: "application/json" }, cache: "no-store",
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const found = cancellableSaleResponseSchema.parse(await response.json()).data;
      setSale(found);
      setSaleFolio(found.folio); setScopeFolio(found.folio); setFilter("ALL");
      setOperationalType(found.sentToLaboratory ? "WARRANTY" : "ADMINISTRATIVE");
      setFinancialDestination(found.paid > 0 ? "REFUND" : "TRANSFER_NEW_ORDER");
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible consultar la orden.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (initialSaleFolio && canRequest) void lookupSale(initialSaleFolio);
  }, [initialSaleFolio, canRequest, lookupSale]);

  async function findSale(event: FormEvent) {
    event.preventDefault();
    await lookupSale(saleFolio);
  }

  async function requestCancellation(event: FormEvent) {
    event.preventDefault(); if (!sale) return; setLoading(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/cancellations`, {
        method: "POST", credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ saleFolio: sale.folio, operationalType, financialDestination, reason }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const request = cancellationRequestResponseSchema.parse(await response.json()).data;
      setSale(null); setSaleFolio(""); setReason("");
      setMessage(`${request.folio} fue enviada a revisión independiente.`);
      await load();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible registrar la solicitud.");
    } finally { setLoading(false); }
  }

  async function decide(item: CancellationRequest, decision: "APPROVE" | "REJECT") {
    setLoading(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/cancellations/${item.id}/decision`, {
        method: "POST", credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ decision, comment: decisionComments[item.id] || "Revisión administrativa confirmada." }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const result = cancellationDecisionResponseSchema.parse(await response.json()).data;
      if (result.authorizationCode) {
        setCodes((current) => ({ ...current, [item.id]: result.authorizationCode! }));
        setMessage("Clave generada. Entrégala al Gerente o Administrador responsable de aplicar la cancelación; se muestra una sola vez.");
      } else setMessage("Solicitud rechazada y registrada en la bitácora.");
      await load();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible registrar la decisión.");
    } finally { setLoading(false); }
  }

  async function execute(item: CancellationRequest) {
    if (!executionConfirmed[item.id]) return;
    setLoading(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/cancellations/${item.id}/execute`, {
        method: "POST", credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ authorizationCode: executeCodes[item.id] || "", frameReceived: Boolean(frameReceived[item.id]) }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const result = cancellationExecutionResponseSchema.parse(await response.json()).data;
      setExecutionConfirmed((current) => ({ ...current, [item.id]: false }));
      setExecuteCodes((current) => ({ ...current, [item.id]: "" }));
      if (result.creditNote?.redemptionCode) {
        setRedemptionCodes((current) => ({ ...current, [item.id]: result.creditNote!.redemptionCode! }));
      }
      setMessage("La orden quedó cancelada e inhabilitada. El movimiento no puede revertirse.");
      await load();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible ejecutar la cancelación.");
    } finally { setLoading(false); }
  }

  async function printDocument(item: CancellationRequest, type: "CANCELLATION_TICKET" | "CREDIT_NOTE", download: boolean) {
    const redemptionCode = redemptionCodes[item.id];
    if (type === "CREDIT_NOTE" && !redemptionCode) {
      setMessage("La reimpresión no incluye el código secreto. El código original solo se entrega al aplicar la cancelación.");
    }
    const filename = `${type === "CREDIT_NOTE" ? item.creditNote?.folio : item.folio}.pdf`;
    const bytes = await buildCancellationTicketPdf(item, type === "CREDIT_NOTE", redemptionCode);
    if (download) downloadPdf(bytes, filename);
    else await printReceiptPdf(bytes, filename);
    const response = await fetch(`${apiBaseUrl}/api/v1/cancellations/${item.id}/print`, {
      method: "POST", credentials: "include",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ documentType: type, copies: 1 }),
    });
    if (response.ok) actionResponseSchema.parse(await response.json());
  }

  async function applyCredit(event: FormEvent) {
    event.preventDefault(); setLoading(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/credit-notes/apply`, {
        method: "POST", credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ redemptionCode: creditCode, destinationSaleFolio: destinationSale, amount: Number(creditAmount) }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const note = creditNoteSchema.parse((await response.json() as { data: unknown }).data);
      setMessage(`Se aplicó la nota ${note.folio}. Saldo disponible: ${money.format(note.balance)}.`);
      setCreditCode(""); setDestinationSale(""); setCreditAmount(""); await load();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible aplicar la nota de crédito.");
    } finally { setLoading(false); }
  }

  async function confirmRefund(item: CancellationRequest) {
    if (!item.creditNote) return; setLoading(true); setMessage("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/credit-notes/${item.creditNote.id}/refund`, {
        method: "POST", credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ externalReference: refundReferences[item.id] || "" }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      creditNoteSchema.parse((await response.json() as { data: unknown }).data);
      setMessage("La devolución quedó confirmada con su referencia externa."); await load();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible confirmar la devolución.");
    } finally { setLoading(false); }
  }

  return <section className="admin-shell cancellation-shell" aria-labelledby="cancellation-title">
    <div className="section-heading"><div><p className="eyebrow">Control independiente</p><h1 id="cancellation-title">Cancelaciones y notas de crédito</h1><p>La solicitud, autorización y ejecución quedan separadas y registradas.</p></div><button className="secondary-button" onClick={onClose}>Volver al inicio</button></div>
    {message && <p className="form-message info-message" aria-live="polite">{message}</p>}

    {canRequest && <form className="operation-panel cancellation-request" onSubmit={sale ? requestCancellation : findSale}>
      <div className="panel-heading"><div><h2>Solicitar cancelación</h2><p>Gerencia o Administración pueden solicitarla. Otra persona de Supervisión o Administración debe autorizarla.</p></div></div>
      <div className="inline-fields"><label>Número de orden<input disabled={loading || Boolean(sale)} maxLength={80} onChange={(event) => setSaleFolio(event.target.value)} placeholder="Ejemplo: Venta-PRUEBAS-01-0001" required value={saleFolio} /></label>{!sale && <button className="secondary-button" disabled={loading}>Consultar orden</button>}</div>
      {sale && <><article className="sale-cancellation-preview"><div><span>{sale.folio}</span><strong>{sale.customerName}</strong><small>{sale.frameDescription} · {sale.branchName}</small></div><div><span>Pagado</span><strong>{money.format(sale.paid)}</strong><small>Total: {money.format(sale.total)} · Saldo pendiente: {money.format(sale.balance)}</small><small>{sale.sentToLaboratory ? "Ya fue enviada a laboratorio" : "Aún no se envía a laboratorio"}</small></div></article>
        <p>{sale.frameReturnRequired ? "Para aplicar la cancelación se deberá confirmar la recepción física del armazón de catálogo." : sale.frameOrigin === "CUSTOMER" ? "Armazón del cliente: no se agregará al inventario de la óptica." : "El armazón no tiene una salida de inventario que reintegrar."} Los lentes no se reintegran al inventario.</p>
        {sale.invoiceRequested && <p className="lens-notice">Esta orden requiere revisión fiscal. Cancelar la venta no cancela automáticamente su factura.</p>}
        <div className="field-grid"><label>Tipo<select disabled value={operationalType}><option value="ADMINISTRATIVE">Administrativa · sin merma de lentes</option><option value="WARRANTY">Garantía · con merma de lentes</option></select></label><label>Destino del importe<select disabled={sale.paid === 0} onChange={(event) => setFinancialDestination(event.target.value as "REFUND" | "TRANSFER_NEW_ORDER")} value={financialDestination}><option value="REFUND">Solicitar devolución</option><option value="TRANSFER_NEW_ORDER">Traspasar a una orden nueva</option></select></label></div>
        <label>Motivo detallado<textarea maxLength={1000} minLength={10} onChange={(event) => setReason(event.target.value)} required rows={3} value={reason} /></label>
        <p>{sale.paid === 0 ? "No hay importe pagado que devolver o traspasar." : financialDestination === "REFUND" ? `La devolución de ${money.format(sale.paid)} quedará pendiente; esta solicitud no entrega dinero ni abre la caja.` : `Se generará una nota por ${money.format(sale.paid)} para aplicar a una orden nueva, después de ejecutar la cancelación.`}</p>
        <div className="button-row"><button className="primary-button" disabled={loading}>Enviar a revisión</button><button className="text-button" onClick={() => { setSale(null); setSaleFolio(""); }} type="button">Elegir otra orden</button></div></>}
    </form>}

    <section className="operation-panel"><div className="panel-heading"><div><h2>Bandeja auditable</h2><p>Las claves duran 15 minutos, son de un solo uso y están ligadas al importe y la orden.</p></div><div className="inline-fields compact-filter"><select aria-label="Estado" disabled={loading} onChange={(event) => setFilter(event.target.value)} value={filter}><option value="ALL">Todos</option><option value="PENDING">Pendientes</option><option value="APPROVED">Autorizadas</option><option value="EXECUTED">Aplicadas</option><option value="REJECTED">Rechazadas</option><option value="EXPIRED">Vencidas</option></select><button className="secondary-button compact-button" disabled={loading} onClick={() => void load()} type="button">Actualizar</button></div></div>
      {scopeFolio && <p>Orden consultada: <strong>{scopeFolio}</strong> <button className="text-button" disabled={loading} type="button" onClick={() => { setScopeFolio(""); setSale(null); setSaleFolio(""); setMessage(""); }}>Ver todas las solicitudes</button></p>}
      <div className="cancellation-list">{items.map((item) => <article className="cancellation-card" key={item.id}>
        <div className="cancellation-summary"><div><span className={`status-pill status-${item.status.toLowerCase()}`}>{statusLabels[item.status]}</span><h3>{item.folio} · {item.saleFolio}</h3><p>{item.customerName} · {item.branchName}</p><small>{item.operationalType === "WARRANTY" ? "Garantía con merma de lentes" : "Administrativa sin merma de lentes"} · {item.reason}</small></div><div className="cancellation-amount"><span>{["PENDING", "REJECTED"].includes(item.status) ? "Importe solicitado" : "Importe autorizado"}</span><strong>{money.format(item.authorizedAmount)}</strong><small>{item.financialDestination === "REFUND" ? "Devolución" : item.financialDestination === "TRANSFER_NEW_ORDER" ? "Traspaso" : "Sin importe"}</small></div></div>
        {item.creditNote?.destination === "REFUND" && <p className="lens-notice">{item.creditNote.status === "REFUNDED" ? "Devolución registrada como realizada." : "Devolución pendiente de realizar. Cancelar la venta no entrega dinero automáticamente."}</p>}
        <p className="cancellation-audit">Solicitó: {item.requestedBy} · {eventDate(item.requestedAt)}{item.decidedAt && <> | Revisó: {item.decidedBy} · {eventDate(item.decidedAt)}</>}{item.executedAt && <> | Aplicó: {item.executedBy} · {eventDate(item.executedAt)}</>} (hora de Hermosillo)</p>
        {item.status === "PENDING" && item.requestedByUserId === session.userId && <p className="lens-notice">Esta solicitud es tuya. Debe autorizarla otra persona de Supervisión o Administración.</p>}
        {canApprove && item.status === "PENDING" && item.requestedByUserId && item.requestedByUserId !== session.userId && <div className="decision-box"><label>Comentario de revisión<input onChange={(event) => setDecisionComments({ ...decisionComments, [item.id]: event.target.value })} placeholder="Resultado de la revisión" value={decisionComments[item.id] || ""} /></label><div className="button-row"><button className="primary-button" disabled={loading} onClick={() => void decide(item, "APPROVE")} type="button">Autorizar y generar clave</button><button className="danger-button" disabled={loading} onClick={() => void decide(item, "REJECT")} type="button">Rechazar</button></div></div>}
        {codes[item.id] && <div className="one-time-code"><span>Clave de un solo uso</span><strong>{codes[item.id]}</strong><small>Entrégala de forma segura al Gerente o Administrador que aplicará la cancelación. No volverá a mostrarse.</small></div>}
        {canExecute && item.status === "APPROVED" && <div className="execution-box"><p>Orden: <strong>{item.saleFolio}</strong> · Importe: <strong>{money.format(item.authorizedAmount)}</strong> · Destino: {item.financialDestination === "REFUND" ? "Devolución pendiente de realizar" : item.financialDestination === "TRANSFER_NEW_ORDER" ? "Nota para una orden nueva" : "Sin importe"}</p><label>Clave de autorización<input autoComplete="off" maxLength={8} onChange={(event) => setExecuteCodes({ ...executeCodes, [item.id]: event.target.value.toUpperCase() })} value={executeCodes[item.id] || ""} /></label>{item.frameReturnRequired && <label className="check-line"><input checked={Boolean(frameReceived[item.id])} onChange={(event) => setFrameReceived({ ...frameReceived, [item.id]: event.target.checked })} type="checkbox" />Confirmo que recibí físicamente el armazón</label>}<label className="check-line"><input type="checkbox" checked={Boolean(executionConfirmed[item.id])} onChange={(event) => setExecutionConfirmed({ ...executionConfirmed, [item.id]: event.target.checked })} />Revisé la orden y el importe. Entiendo que la cancelación definitiva no se puede revertir.</label><button className="danger-button" disabled={loading || !executionConfirmed[item.id] || (executeCodes[item.id] || "").length !== 8 || (item.frameReturnRequired && !frameReceived[item.id])} onClick={() => void execute(item)} type="button">Aplicar cancelación definitiva</button></div>}
        {redemptionCodes[item.id] && <div className="one-time-code credit-code"><span>Código de nota de crédito</span><strong>{redemptionCodes[item.id]}</strong><small>Se muestra una sola vez. Entrégalo únicamente al responsable del movimiento.</small></div>}
        {item.status === "EXECUTED" && <div className="document-actions"><button className="secondary-button" onClick={() => void printDocument(item, "CANCELLATION_TICKET", true)}>Descargar cancelación</button><button className="secondary-button" onClick={() => void printDocument(item, "CANCELLATION_TICKET", false)}>Imprimir cancelación</button>{item.creditNote && <><button className="secondary-button" onClick={() => void printDocument(item, "CREDIT_NOTE", true)}>Descargar nota</button><button className="secondary-button" onClick={() => void printDocument(item, "CREDIT_NOTE", false)}>Imprimir nota</button></>}{canConfirmRefund && item.creditNote?.status === "REFUND_PENDING" && <div className="refund-confirm"><input onChange={(event) => setRefundReferences({ ...refundReferences, [item.id]: event.target.value })} placeholder="Referencia de devolución" value={refundReferences[item.id] || ""} /><button className="primary-button" onClick={() => void confirmRefund(item)} type="button">Confirmar devolución realizada</button></div>}</div>}
      </article>)}{!loading && items.length === 0 && <p className="empty-state">No hay cancelaciones con este estado.</p>}</div>
    </section>

    {canExecute && <form className="operation-panel credit-application" onSubmit={applyCredit}><div className="panel-heading"><div><h2>Aplicar nota a una orden nueva</h2><p>Solo admite una orden graduada activa de la misma sucursal y hasta el saldo pendiente.</p></div></div><div className="field-grid"><label>Código de nota<input maxLength={10} onChange={(event) => setCreditCode(event.target.value.toUpperCase())} required value={creditCode} /></label><label>Nueva orden<input maxLength={80} onChange={(event) => setDestinationSale(event.target.value)} required value={destinationSale} /></label><label>Importe<input min="0.01" onChange={(event) => setCreditAmount(event.target.value)} required step="0.01" type="number" value={creditAmount} /></label></div><button className="primary-button" disabled={loading}>Aplicar saldo</button></form>}
  </section>;
}
